import { SITE } from "../config/content";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "marubazu_admin_token";

export function whatsappLink(message) {
  return `https://wa.me/${SITE.whatsappNumber}?text=${encodeURIComponent(message)}`;
}

export function genericWhatsAppLink() {
  return whatsappLink("Hi Marubazu! I want to know more about the live trading workspace.");
}

export function leadWhatsAppLink(phone, message) {
  return `https://wa.me/91${phone}?text=${encodeURIComponent(message)}`;
}

export async function saveLead(data) {
  const res = await fetch(`${API}/leads/save`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatDetail(err.detail) || "Something went wrong. Please try again.");
  }
  return res.json();
}

export async function getActiveBatch() {
  try {
    const res = await fetch(`${API}/batches/active`);
    if (!res.ok) return null;
    const d = await res.json();
    return d.batch || null;
  } catch {
    return null;
  }
}

// ---------- Admin ----------

export function getAdminToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}

export function setAdminToken(t) {
  localStorage.setItem(TOKEN_KEY, t);
}

export function clearAdminToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function adminFetch(path, { method = "GET", body, raw = false } = {}) {
  const headers = { Authorization: `Bearer ${getAdminToken()}` };
  if (body !== undefined) headers["Content-Type"] = "application/json";
  const res = await fetch(`${API}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (res.status === 401) {
    clearAdminToken();
    window.dispatchEvent(new Event("admin:logout"));
    throw new Error("Session expired. Please log in again.");
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatDetail(err.detail) || `Request failed (${res.status})`);
  }
  return raw ? res : res.json();
}

export async function adminLogin(email, password) {
  const res = await fetch(`${API}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, password }),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(formatDetail(err.detail) || "Login failed");
  }
  return res.json();
}

export const getLeads = () => adminFetch("/admin/leads");
export const getStats = () => adminFetch("/admin/stats");
export const updateLead = (id, patch) => adminFetch(`/admin/leads/${id}`, { method: "PATCH", body: patch });
export const logCall = (id, outcome, note) => adminFetch(`/admin/leads/${id}/log-call`, { method: "POST", body: { outcome, note } });
export const bulkUpdateLeads = (ids, patch) => adminFetch("/admin/leads/bulk", { method: "POST", body: { ids, ...patch } });
export const getBatches = () => adminFetch("/admin/batches");
export const createBatch = (b) => adminFetch("/admin/batches", { method: "POST", body: b });
export const updateBatch = (id, b) => adminFetch(`/admin/batches/${id}`, { method: "PATCH", body: b });
export const deleteBatch = (id) => adminFetch(`/admin/batches/${id}`, { method: "DELETE" });
export const seedTestLeads = () => adminFetch("/admin/seed-test-leads", { method: "POST" });
export const deleteTestLeads = () => adminFetch("/admin/test-leads", { method: "DELETE" });

export async function downloadLeadsXlsx(ids) {
  const res = await adminFetch("/admin/leads/export", { method: "POST", body: { ids }, raw: true });
  const blob = await res.blob();
  const dispo = res.headers.get("Content-Disposition") || "";
  const match = dispo.match(/filename="?([^";]+)"?/);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = match ? match[1] : "marubazu-leads.xlsx";
  a.click();
  URL.revokeObjectURL(url);
}

export function confirmationWhatsAppLink({ name, city, experience, problems }) {
  let msg = `Hi Marubazu! I'm ${name} from ${city}.`;
  if (experience) msg += ` ${experience.charAt(0).toUpperCase() + experience.slice(1)}.`;
  if (problems && problems.length) msg += ` My biggest problem is ${problems.join(" and ")}.`;
  msg += " I'd like to book my free Trading Check-up.";
  return whatsappLink(msg);
}

function formatDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg ? e.msg.replace(/^Value error, /, "") : "")).filter(Boolean).join(" ");
  return String(detail);
}
